"use client";

import { useEffect, useRef, useState } from "react";
import type { Template, TemplateNode } from "../types/template";

// --- Internal helpers ---

type ScaleSet = {
  font: number;
  spacing: number;
  layout: number;
  /** True while a split-column template renders in dense (narrow) phone mode. */
  dense?: boolean;
  /**
   * True while a legacy-scroll template renders below the phone breakpoint.
   * Phone mode replaces the uniform "shrink everything a little" scaling with
   * a mobile-app treatment: a type ramp that compresses the 1080-canvas
   * hierarchy into readable phone sizes, aggressive spacing compaction, and
   * structural restacking of group layouts (see `resolveGroupLayout` and the
   * `.menuraq-phone` layer in `app/globals.css`).
   */
  phone?: boolean;
  phoneColumns?: 1 | 2;
};

/**
 * Container width below which a legacy-scroll template switches to the
 * phone-app treatment. 640 covers every real phone (360–430) plus the small
 * phone-sized preview iframes in the dashboard gallery, while leaving the
 * tablet/desktop/TV sizes and every paged-flip template untouched.
 */
export const PHONE_BREAKPOINT = 640;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

/**
 * Recursively checks whether any template node splits items or categories
 * into two columns. Some split templates only set the node-level
 * `splitItemsColumns` / `splitCategoriesColumns` props (e.g. Elegant Bistro
 * Classic) while leaving the template-level `splitColumns` flag unset, so
 * dense-split responsive handling must look at the node tree too.
 */
function templateUsesSplitColumns(node: TemplateNode | undefined): boolean {
  if (!node) return false;
  if (
    node.props?.splitItemsColumns ||
    node.props?.splitCategoriesColumns ||
    Array.isArray(node.children) &&
      node.children.some((child) => templateUsesSplitColumns(child))
  ) {
    return true;
  }
  return false;
}

/**
 * Readable font floor for split-column templates in dense (narrow) mode.
 * Split columns stay side-by-side on phones, leaving each column roughly half
 * the viewport width, so text must not shrink to the template's tablet-tuned
 * minFontScale (often 0.72) or titles and category names become unreadable.
 */
const SPLIT_DENSE_READABLE_FONT_FLOOR = 0.88;

/**
 * Soft exponential scaling
 * Prevents aggressive shrinking on small screens
 */
function softScale(
  ratio: number,
  strength = 0.6 // lower = gentler scaling
) {
  return Math.pow(ratio, strength);
}

// --- Public hooks ---

export function useResponsiveScale(template: Template) {
  const ref = useRef<HTMLDivElement>(null);
  const [viewportWidth, setViewportWidth] = useState(0);

  const [scales, setScales] = useState<ScaleSet>({
    font: 1,
    spacing: 1,
    layout: 1,
    dense: false,
    phone: false,
    phoneColumns: 1,
  });

  const splitColumnsBreakpoint = template.responsive?.splitColumnsBreakpoint ?? 960;
  const usesSplitColumns = Boolean(
    template.splitColumns ||
    (Array.isArray(template.nodes) && template.nodes.some(templateUsesSplitColumns)),
  );
  const isDenseSplitMode = Boolean(
    usesSplitColumns && viewportWidth > 0 && viewportWidth < splitColumnsBreakpoint,
  );

  useEffect(() => {
    if (!ref.current) return;

    const responsive = template.responsive || {};
    const isPagedFlip = template.engine === "paged-flip" || Boolean(template.pagedFlip || template.pagedNodes);
    const baseWidth = typeof (responsive as { baseWidth?: unknown }).baseWidth === "number"
      ? (responsive as { baseWidth: number }).baseWidth
      : isPagedFlip
        ? 460
        : template.canvas?.width ?? 1080;

    const observer = new ResizeObserver(([entry]) => {
      const containerWidth = entry.contentRect.width;

      if (!containerWidth || containerWidth <= 0) return;
      setViewportWidth(containerWidth);

      /**
       * Raw width ratio
       */
      const ratio = containerWidth / baseWidth;
      // Phone mode is a legacy-scroll concept: paged-flip has its own narrow
      // page handling (baseWidth 460 + narrow-page clamp floors below).
      const isPhone = !isPagedFlip && containerWidth < PHONE_BREAKPOINT;
      const isDenseSplitMode = Boolean(usesSplitColumns && containerWidth < splitColumnsBreakpoint);
      const denseRatio = isDenseSplitMode ? clamp(containerWidth / splitColumnsBreakpoint, 0.5, 1) : 1;
      const isNarrowFlipbookPage = isPagedFlip && containerWidth < 520;
      const defaultMinFontScale = isPhone ? 0.42 : isPagedFlip ? (isNarrowFlipbookPage ? 0.58 : 0.68) : 0.94;
      const defaultMaxFontScale = isPhone ? 1 : isPagedFlip ? 1 : 1.12;
      const defaultMinSpacingScale = isPhone ? 0.32 : isPagedFlip ? (isNarrowFlipbookPage ? 0.5 : 0.58) : 0.7;
      const defaultMaxSpacingScale = isPhone ? 0.85 : isPagedFlip ? 1.05 : 1.25;
      const defaultMinLayoutScale = isPhone ? 0.4 : isPagedFlip ? (isNarrowFlipbookPage ? 0.58 : 0.68) : 0.82;
      const defaultMaxLayoutScale = isPhone ? 0.9 : isPagedFlip ? 1.05 : 1.2;

      /**
       * SOFT SCALES (non-linear)
       */
      const soft = softScale(ratio);

      /**
       * FONT SCALE
       * - Protect readability
       * - Shrinks very gently
       */
      let fontScale = clamp(
        soft,
        responsive.minFontScale ?? defaultMinFontScale,
        responsive.maxFontScale ?? defaultMaxFontScale
      );

      /**
       * SPACING SCALE
       * - Shrinks more on mobile
       * - Prevents huge whitespace
       */
      let spacingScale = clamp(
        soft * 0.9,
        responsive.minSpacingScale ?? defaultMinSpacingScale,
        responsive.maxSpacingScale ?? defaultMaxSpacingScale
      );

      /**
       * LAYOUT SCALE
       * - Structural dimensions
       * - Should change the least
       */
      let layoutScale = clamp(
        soft * 0.95,
        responsive.minLayoutScale ?? defaultMinLayoutScale,
        responsive.maxLayoutScale ?? defaultMaxLayoutScale
      );

      if (isDenseSplitMode) {
        const splitFontMultiplier = clamp(0.72 + denseRatio * 0.28, 0.72, 1);
        const splitSpacingMultiplier = clamp(0.58 + denseRatio * 0.42, 0.58, 1);
        const splitLayoutMultiplier = clamp(0.74 + denseRatio * 0.26, 0.74, 1);

        fontScale = clamp(
          fontScale * splitFontMultiplier,
          Math.max(
            responsive.minFontScale ?? defaultMinFontScale,
            SPLIT_DENSE_READABLE_FONT_FLOOR,
          ),
          responsive.maxFontScale ?? defaultMaxFontScale,
        );
        spacingScale = clamp(
          spacingScale * splitSpacingMultiplier,
          Math.min(responsive.minSpacingScale ?? defaultMinSpacingScale, 0.56),
          responsive.maxSpacingScale ?? defaultMaxSpacingScale,
        );
        layoutScale = clamp(
          layoutScale * splitLayoutMultiplier,
          Math.min(responsive.minLayoutScale ?? defaultMinLayoutScale, 0.7),
          responsive.maxLayoutScale ?? defaultMaxLayoutScale,
        );
      }

      setScales({
        font: Number(fontScale.toFixed(3)),
        spacing: Number(spacingScale.toFixed(3)),
        layout: Number(layoutScale.toFixed(3)),
        dense: isDenseSplitMode,
        phone: isPhone,
        phoneColumns: responsive.phoneColumns === 2 ? 2 : 1,
      });
    });

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [template.canvas?.width, template.engine, template.nodes, template.pagedFlip, template.pagedNodes, template.responsive, template.splitColumns]);

  return {
    ref,
    scales,
    viewportWidth,
    isCompactSplitLayout: viewportWidth > 0 && viewportWidth < splitColumnsBreakpoint,
    isDenseSplitMode,
  };
}


export function useResponsiveScaleCanvas(template: Template) {
  const ref = useRef<HTMLDivElement>(null);
  const [scales, setScales] = useState({
    font: 1,
    spacing: 1,
    layout: 1,
  });

  useEffect(() => {
    function updateScale() {
      if (!ref.current) return;

      const containerWidth = ref.current.offsetWidth;
      const baseWidth = template.canvas?.width ?? 1080;

      // Raw scale ratio
      const ratio = containerWidth / baseWidth;

      /**
       * FONT SCALE RULES
       * - Never go below 0.92 (readability floor)
       * - Allow slight upscale on large screens
       */
      const fontScale = clamp(
        ratio,
        0.92,   // minimum readable size
        1.15    // optional upscale on wide screens
      );

      /**
       * SPACING SCALE
       * - Can shrink more aggressively
       */
      const spacingScale = clamp(ratio, 0.75, 1.3);

      /**
       * LAYOUT SCALE
       * - Structural sizing
       */
      const layoutScale = clamp(ratio, 0.8, 1.25);

      setScales({
        font: fontScale,
        spacing: spacingScale,
        layout: layoutScale,
      });
    }

    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, [template.canvas?.width]);

  return { ref, scales };
}
